import { Phone, Mail, MapPin, CalendarCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";

export default function CTAFooter({ onOpenBooking }) {
  return (
    <>
      {/* Final CTA */}
      <section
        id="contact"
        data-testid="cta-section"
        className="relative overflow-hidden bg-[#0B3A5B] text-white py-20 lg:py-24"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_auto-detailing-pro-16/artifacts/5z9mkx1f_j-z-aKwjqJjpUBQ-unsplash.jpg')",
          }}
        />
        <div className="absolute inset-0 bg-[#0B3A5B]/85" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#072337]/60 via-transparent to-[#1E88C8]/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center reveal">
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-[#1E88C8] font-semibold">
                Prendre contact
              </p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl mt-3 text-white">
                Prêt à retrouver un intérieur impeccable&nbsp;?
              </h2>
              <p className="mt-5 text-white/80 text-base leading-relaxed max-w-xl">
                Appelez pour un rendez-vous rapide ou demandez un devis en ligne.
                Réponse sous 24h, paiement à la prestation.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${BUSINESS.phone}`}
                  data-testid="cta-call-button"
                  className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-white text-[#0B3A5B] font-semibold hover:bg-white/95"
                >
                  <Phone className="h-5 w-5" />
                  {BUSINESS.phoneDisplay}
                </a>
                <Button
                  data-testid="cta-booking-button"
                  onClick={onOpenBooking}
                  className="h-14 px-7 rounded-full bg-[#1E88C8] hover:bg-[#1670a8] text-white font-semibold"
                >
                  <CalendarCheck className="h-5 w-5 mr-2" />
                  Prendre rendez-vous
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ContactTile icon={<Phone className="h-5 w-5" />} label="Téléphone" value={BUSINESS.phoneDisplay} href={`tel:${BUSINESS.phone}`} />
              <ContactTile icon={<Mail className="h-5 w-5" />} label="Email" value={BUSINESS.email} href={`mailto:${BUSINESS.email}`} />
              <ContactTile icon={<MapPin className="h-5 w-5" />} label="Zone" value={BUSINESS.region} />
              <ContactTile icon={<CalendarCheck className="h-5 w-5" />} label="Sur RDV" value="7j / 7 — Service à domicile" />
            </div>
          </div>
        </div>
      </section>

      {/* Google review CTA */}
      <section
        data-testid="google-review-section"
        className="relative bg-white py-14 lg:py-16 border-t border-[#0B3A5B]/10"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex justify-center gap-0.5 mb-4" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-5 w-5 fill-[#FBBC04] text-[#FBBC04]" />
            ))}
          </div>
          <h3 className="font-display text-2xl sm:text-3xl text-[#0B3A5B]">
            Votre avis fait toute la différence
          </h3>
          <p className="mt-3 text-base text-[#2F3438]/75 leading-relaxed">
            Votre satisfaction est essentielle. Si vous êtes satisfait de la prestation,
            quelques secondes suffisent pour partager votre expérience et aider Hingant
            Clean à se faire connaître.
          </p>
          <a
            href="https://g.page/r/CYnLwJG8BuS0EAE/review"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-review-button"
            className="btn-press mt-7 inline-flex items-center justify-center gap-2.5 h-12 sm:h-14 px-6 sm:px-7 rounded-full bg-white border-2 border-[#0B3A5B]/10 text-[#0B3A5B] font-semibold text-base shadow-[0_10px_30px_-12px_rgba(11,58,91,0.25)] hover:shadow-[0_20px_50px_-12px_rgba(66,133,244,0.35)] hover:border-[#4285F4]/30 hover:-translate-y-0.5 transition-all"
          >
            <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden>
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.5 0 10.3-1.9 13.9-5l-6.4-5.4c-2 1.5-4.6 2.4-7.5 2.4-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.7 5l6.4 5.4C42.6 35.4 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            <span>Laisser un avis Google</span>
            <Star className="h-4 w-4 fill-[#FBBC04] text-[#FBBC04]" />
          </a>
          <p className="mt-3 text-xs text-[#2F3438]/55">Merci pour votre soutien ❤</p>
        </div>
      </section>

      <footer
        data-testid="site-footer"
        className="bg-[#0B3A5B] text-white/70 border-t border-white/10 py-8"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-6 sm:flex-row sm:gap-4 items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BUSINESS.logo} alt="Hingant Clean" className="h-16 w-auto object-contain" />
            <div className="text-sm">
              <div className="font-semibold text-white font-display">{BUSINESS.name}</div>
              <div className="text-xs text-white/60">Nettoyage automobile à domicile · {BUSINESS.region}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram Hingant Clean"
              className="h-11 w-11 rounded-full bg-white/5 hover:bg-white/15 border border-white/15 text-white inline-flex items-center justify-center transition-all hover:-translate-y-0.5"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </a>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="Facebook Hingant Clean"
              className="h-11 w-11 rounded-full bg-white/5 hover:bg-white/15 border border-white/15 text-white inline-flex items-center justify-center transition-all hover:-translate-y-0.5"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
              </svg>
            </a>
          </div>

          <div className="text-xs text-white/50 text-center sm:text-right">
            © {new Date().getFullYear()} Hingant Clean<br className="sm:hidden" /> — Tous droits réservés
          </div>
        </div>
      </footer>
    </>
  );
}

function ContactTile({ icon, label, value, href }) {
  const content = (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition">
      <div className="h-9 w-9 rounded-lg bg-[#1E88C8]/20 text-[#1E88C8] flex items-center justify-center mb-3">
        {icon}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-white/50">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white break-all">{value}</div>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
}
